<template>
  <WrappedModalContent :slides="slides" />
</template>

<script setup lang="ts">
import WrappedPage1 from "@/components/Wrapped/Page/1.vue";
import WrappedPage2 from "@/components/Wrapped/Page/2.vue";
import WrappedPage3 from "@/components/Wrapped/Page/3.vue";
import WrappedPage4 from "@/components/Wrapped/Page/4.vue";
import WrappedPage5 from "@/components/Wrapped/Page/5.vue";
import WrappedPage6 from "@/components/Wrapped/Page/6.vue";
import WrappedPage7 from "@/components/Wrapped/Page/7.vue";
import WrappedPage8 from "@/components/Wrapped/Page/8.vue";
import WrappedPage9 from "@/components/Wrapped/Page/9.vue";
import WrappedPage10 from "@/components/Wrapped/Page/10.vue";
import WrappedPage14 from "@/components/Wrapped/Page/14.vue";
import WrappedPage15 from "@/components/Wrapped/Page/15.vue";
import WrappedPage16 from "@/components/Wrapped/Page/16.vue";

import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { memberQueries } from "~/domains/member/queries";
import { Campaign } from "~/lib/campaigns";

const emit = defineEmits<{
  (e: "progress", progress: number): void;
}>();

const numberFormatter = Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const wrapped = useWrappedUp();

const { data: self } = useQuery(memberQueries.self());
const queryClient = useQueryClient();

const [impactInDate, familyInDate] = await Promise.all([
  queryClient
    .ensureQueryData(
      memberQueries.impactInDate({
        memberId: self.value!.id,
        startDate: "2024-01-01",
        endDate: "2024-12-31",
      }),
    )
    .then((res) => res.filter((item) => item.campaignId !== Campaign.OVI)),

  queryClient.ensureQueryData(
    memberQueries.familyInDate({
      memberId: self.value!.id,
      startDate: "2024-01-01",
      endDate: "2024-12-31",
    }),
  ),
]);

function pointsPerCampaign(campaignId: number) {
  const index = impactInDate.findIndex(
    (item) => item.campaignId === campaignId,
  );
  if (index == -1) {
    return 0;
  } else {
    return impactInDate[index].impactValue;
  }
}

function amountPerCampaign(campaignId: number) {
  const index = impactInDate.findIndex(
    (item) => item.campaignId === campaignId,
  );
  if (index == -1) {
    return 0;
  } else {
    return impactInDate[index].totalAmount;
  }
}

const sorted = impactInDate.toSorted((a, b) => b.totalAmount - a.totalAmount);

const components = (
  [
    {
      component: WrappedPage1,
      active: true,
      props: null,
    },
    {
      component: WrappedPage2,
      active: amountPerCampaign(Campaign.PF) > 0,
      props: {
        wrappeddata: numberFormatter.format(pointsPerCampaign(Campaign.PF)),
      },
    },
    {
      component: WrappedPage3,
      active: amountPerCampaign(Campaign.PF) > 0,
      props: {
        wrappeddata: numberFormatter.format(
          amountPerCampaign(Campaign.PF) * 15,
        ),
      },
    },
    { component: WrappedPage2, active: true, props: null },
    { component: WrappedPage4, active: true, props: null },
    {
      component: WrappedPage5,
      active: amountPerCampaign(Campaign.TFTF) > 0,
      props: {
        wrappeddata: numberFormatter.format(pointsPerCampaign(Campaign.TFTF)),
      },
    },
    {
      component: WrappedPage6,
      active: amountPerCampaign(Campaign.TFTF) > 0,
      props: {
        wrappeddata: [
          numberFormatter.format(amountPerCampaign(Campaign.TFTF) * 11.2),
          numberFormatter.format(pointsPerCampaign(Campaign.TFTF) * 267),
        ],
      },
    },
    {
      component: WrappedPage7,
      active: amountPerCampaign(Campaign.TFTF) > 0,
      props: {
        wrappeddata: numberFormatter.format(
          amountPerCampaign(Campaign.TFTF) * 44.49,
        ),
      },
    },
    { component: WrappedPage5, active: true, props: null },
    {
      component: WrappedPage8,
      active: amountPerCampaign(Campaign.TFTF) > 0,
      props: {
        wrappeddata: numberFormatter.format(
          amountPerCampaign(Campaign.TFTF) * 10.5,
        ),
      },
    },
    {
      component: WrappedPage9,
      active: pointsPerCampaign(Campaign.SARTHAK) > 0,
      props: {
        wrappeddata: numberFormatter.format(
          pointsPerCampaign(Campaign.SARTHAK),
        ),
      },
    },
    { component: WrappedPage9, active: true, props: null },
    { component: WrappedPage10, active: true, props: null },
    {
      component: WrappedPage14,
      active: sorted.length > 0,
      props: { wrappeddata: sorted },
    },
    {
      component: WrappedPage15,
      active: familyInDate.count > 0,
      props: { wrappeddata: familyInDate.count },
    },
    {
      component: WrappedPage16,
      active: true,
      props: {
        wrappeddata: sorted
          .filter((item) => item.impactValue > 0)
          .map((item) => ({
            campaignId: item.campaignId,
            impact: numberFormatter.format(item.impactValue),
          })),
      },
    },
  ] as {
    component: Component;
    active: boolean;
    props: Record<string, any> | null;
  }[]
).filter((item) => item.active);

const total = components.length;
let loaded = 0;
emit("progress", 0);

const slides = await Promise.all(
  components.map((item) =>
    wrapped.renderSlide(item.component, item.props).then((slide) => {
      const progress = ++loaded / total;
      emit("progress", progress);
      return slide;
    }),
  ),
);
</script>
